import { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useRestaurantContext } from "@/contexts/RestaurantContext";
import { toast } from "sonner";

export type ExpenseStatus = "open" | "paid" | "cancelled";
export type ExpenseOrigin = "manual" | "cash_withdrawal" | "stock_entry";

export interface ExpenseRow {
  id: string;
  restaurant_id: string;
  title: string;
  description: string | null;
  amount: number;
  status: ExpenseStatus;
  expense_date: string; // date
  due_date: string | null; // date
  paid_at: string | null; // timestamptz
  category_id: string;
  supplier_id: string | null;
  origin: ExpenseOrigin;
  linked_cash_movement_id: string | null;
  linked_stock_movement_id: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  expense_categories?: { name: string } | null;
  suppliers?: { name: string } | null;
}

export interface CreateManualExpenseInput {
  title: string;
  description?: string;
  amount: number;
  expense_date: string; // YYYY-MM-DD
  due_date?: string;
  category_id: string;
  supplier_id: string;
  status: ExpenseStatus;
  paid_at?: string;
}

export interface ExpenseFilters {
  status?: ExpenseStatus | "all";
  origin?: ExpenseOrigin | "all";
  categoryId?: string | "all";
  supplierId?: string | "all";
  startDate?: Date;
  endDate?: Date;
}

function toDateOnly(value: Date) {
  return value.toISOString().slice(0, 10);
}

export function useExpenses(filters: ExpenseFilters) {
  const { activeRestaurant } = useRestaurantContext();
  const [expenses, setExpenses] = useState<ExpenseRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchExpenses = useCallback(async () => {
    if (!activeRestaurant) {
      setExpenses([]);
      return;
    }

    setIsLoading(true);
    try {
      let query = supabase
        .from("expenses" as any)
        .select(
          "id, restaurant_id, title, description, amount, status, expense_date, due_date, paid_at, category_id, supplier_id, origin, linked_cash_movement_id, linked_stock_movement_id, created_by, created_at, updated_at, expense_categories(name), suppliers(name)"
        )
        .eq("restaurant_id", activeRestaurant.id)
        .order("expense_date", { ascending: false })
        .order("created_at", { ascending: false });

      if (filters.status && filters.status !== "all") {
        query = query.eq("status", filters.status);
      }
      if (filters.origin && filters.origin !== "all") {
        query = query.eq("origin", filters.origin);
      }
      if (filters.categoryId && filters.categoryId !== "all") {
        query = query.eq("category_id", filters.categoryId);
      }
      if (filters.supplierId && filters.supplierId !== "all") {
        query = query.eq("supplier_id", filters.supplierId);
      }
      if (filters.startDate) {
        query = query.gte("expense_date", toDateOnly(filters.startDate));
      }
      if (filters.endDate) {
        query = query.lte("expense_date", toDateOnly(filters.endDate));
      }

      const { data, error } = await query;
      if (error) throw error;
      setExpenses(((data as unknown) as ExpenseRow[]) || []);
    } catch (e) {
      console.error("Error fetching expenses", e);
      toast.error("Erro ao carregar despesas");
    } finally {
      setIsLoading(false);
    }
  }, [activeRestaurant, filters.status, filters.origin, filters.categoryId, filters.supplierId, filters.startDate, filters.endDate]);

  useEffect(() => {
    fetchExpenses();
  }, [fetchExpenses]);

  const totals = useMemo(() => {
    const openAmount = expenses
      .filter((e) => e.status === "open")
      .reduce((sum, e) => sum + Number(e.amount), 0);
    const paidAmount = expenses
      .filter((e) => e.status === "paid")
      .reduce((sum, e) => sum + Number(e.amount), 0);
    return { openAmount, paidAmount };
  }, [expenses]);

  const markPaid = useCallback(async (expenseId: string, paidAtISO: string) => {
    try {
      const { error } = await supabase
        .from("expenses" as any)
        .update({ status: "paid", paid_at: paidAtISO })
        .eq("id", expenseId);
      if (error) throw error;
      toast.success("Conta marcada como paga");
      await fetchExpenses();
      return true;
    } catch (e) {
      console.error("Error marking expense paid", e);
      toast.error("Erro ao marcar como paga");
      return false;
    }
  }, [fetchExpenses]);

  const reopen = useCallback(async (expenseId: string) => {
    try {
      const { error } = await supabase
        .from("expenses" as any)
        .update({ status: "open", paid_at: null })
        .eq("id", expenseId);
      if (error) throw error;
      toast.success("Conta reaberta");
      await fetchExpenses();
      return true;
    } catch (e) {
      console.error("Error reopening expense", e);
      toast.error("Erro ao reabrir");
      return false;
    }
  }, [fetchExpenses]);

  const cancel = useCallback(async (expenseId: string) => {
    try {
      const { error } = await supabase
        .from("expenses" as any)
        .update({ status: "cancelled" })
        .eq("id", expenseId);
      if (error) throw error;
      toast.success("Conta cancelada");
      await fetchExpenses();
      return true;
    } catch (e) {
      console.error("Error cancelling expense", e);
      toast.error("Erro ao cancelar");
      return false;
    }
  }, [fetchExpenses]);

  const createManualExpense = useCallback(
    async (input: CreateManualExpenseInput) => {
      if (!activeRestaurant) return null;
      try {
        const { data: auth } = await supabase.auth.getUser();
        const userId = auth.user?.id || null;

        const { data, error } = await supabase
          .from("expenses" as any)
          .insert({
            restaurant_id: activeRestaurant.id,
            title: input.title,
            description: input.description || null,
            amount: input.amount,
            status: input.status,
            expense_date: input.expense_date,
            due_date: input.due_date || null,
            paid_at: input.status === "paid" ? input.paid_at || new Date().toISOString() : null,
            category_id: input.category_id,
            supplier_id: input.supplier_id,
            origin: "manual",
            created_by: userId,
          })
          .select("id")
          .single();

        if (error) throw error;
        toast.success("Despesa criada");
        await fetchExpenses();
        return (data as unknown as { id?: string } | null)?.id || null;
      } catch (e) {
        console.error("Error creating manual expense", e);
        toast.error("Erro ao criar despesa");
        return null;
      }
    },
    [activeRestaurant, fetchExpenses]
  );

  const updateManualExpense = useCallback(
    async (expenseId: string, input: CreateManualExpenseInput) => {
      try {
        const { error } = await supabase
          .from("expenses" as any)
          .update({
            title: input.title,
            description: input.description || null,
            amount: input.amount,
            status: input.status,
            expense_date: input.expense_date,
            due_date: input.due_date || null,
            paid_at: input.status === "paid" ? input.paid_at || new Date().toISOString() : null,
            category_id: input.category_id,
            supplier_id: input.supplier_id,
          })
          .eq("id", expenseId);
        if (error) throw error;
        toast.success("Despesa atualizada");
        await fetchExpenses();
        return true;
      } catch (e) {
        console.error("Error updating manual expense", e);
        toast.error("Erro ao atualizar despesa");
        return false;
      }
    },
    [fetchExpenses]
  );

  return {
    expenses,
    isLoading,
    totals,
    refetch: fetchExpenses,
    markPaid,
    reopen,
    cancel,
    createManualExpense,
    updateManualExpense,
  };
}
